import React, { Fragment } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import moment from 'moment';

import SignInPage from '@Pages/login/signIn';
import AutoLogin from '@Pages/login/autoLogin';
import RegisterPage from '@Pages/login/register';
import RecoverPage from '@Pages/login/recover';
import PasswordPage from '@Pages/login/password';
import NoPermissionDialog from '@Common/NoPermissionDialog';
import FieldModelDialog from '@Common/FieldModelDialog';
import SharedDocument from '@Pages/documentShared'

import LazyRouteName from '@Common/lazyRouteName';
import LazyRouteProtectedName from '@Common/lazyRouteProtectedName';
import Messages from '@Common/messages';
import SnackBar from '@Common/snackBar';
//import Intercom                  from 'react-intercom';
//import { sha256 }                   from 'js-sha256';

import * as routes from '@App/constants/router';

import 'rc-table/assets/index.css';
import 'rc-pagination/assets/index.css';
import 'react-block-ui/style.css';
import 'loaders.css/loaders.min.css';
import 'react-redux-toastr/lib/css/react-redux-toastr.min.css';
import 'react-quill/dist/quill.core.css';
import 'react-quill/dist/quill.snow.css';
import 'react-image-lightbox/style.css';
import '@Assets/css/app.css';
import '@Assets/css/mvf-embedded.css';
import '@Assets/css/nima-loading.css';
import systemConfigs from '@Constants/systemConfigs';
import ReactGA from 'react-ga';

import { getCookie, getMvfThemeOverride, isMvfEmbeddedWindow } from '@Util';
import { prefetchRouteChunks } from '@Util/prefetchLegacyRoutes';

class App extends React.Component {
  componentDidMount() {
    moment.locale(this.props.momentLocale);
    this.prefetchMvfDeepLink();
    this.syncEmbeddedTheme();
    window.addEventListener('message', this.handleMvfNavigationMessage);
    this.initMVFWidget();
    this.postMvfLocationChange();
    this.postMvfReady();
    this.unlistenHistory = this.props.history.listen((location) => {
      ReactGA.pageview(location.pathname);
      this.syncEmbeddedTheme();
      this.initMVFWidget();
      this.postMvfLocationChange();
      this.postMvfReady();
    });
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleMvfNavigationMessage);
    if (this.parentThemeObserver) {
      this.parentThemeObserver.disconnect();
    }
    if (this.unlistenHistory) {
      this.unlistenHistory();
    }
  }

  prefetchMvfDeepLink = () => {
    // No embed do MVF o destino final (legacyNext) já está no cookie antes do
    // bootstrap terminar; baixar o chunk da rota em paralelo às chamadas de
    // /started encurta o deep-link em alguns segundos.
    try {
      if (!isMvfEmbeddedWindow()) return;
      const auth = JSON.parse(getCookie('auth') || '{}');
      const legacyNext = auth.mvfContext && auth.mvfContext.legacyNext;
      prefetchRouteChunks(legacyNext);
    } catch (e) {
      // Prefetch nunca deve quebrar o boot.
    }
  };

  isEmbeddedInMvf = () => {
    // Visual MVF quando embedded de fato ou quando /legacy e aberto direto
    // no dev server para simular o shell do MVF.
    return isMvfEmbeddedWindow() || window.location.pathname.startsWith('/legacy/');
  };

  syncEmbeddedTheme = () => {
    const isEmbedded = this.isEmbeddedInMvf();
    document.body.classList.toggle('usecore-mvf-embedded', isEmbedded);
    document.body.classList.toggle('usecore-mvf-dark', isEmbedded && this.isMvfDarkTheme());

    if (isEmbedded) {
      this.observeParentTheme();
    }
  };

  isMvfDarkTheme = () => {
    const themeOverride = getMvfThemeOverride();
    if (themeOverride === 'dark' || themeOverride === 'light') {
      this.persistMvfTheme(themeOverride);
      return themeOverride === 'dark';
    }

    try {
      if (window.parent && window.parent !== window) {
        const parentHtml = window.parent.document && window.parent.document.documentElement;
        if (parentHtml) {
          return parentHtml.classList.contains('dark');
        }
      }
    } catch (e) {
      // Cross-origin fallback below.
    }

    try {
      const savedTheme = window.localStorage && window.localStorage.getItem('theme');
      if (savedTheme === 'dark') return true;
      if (savedTheme === 'light') return false;
    } catch (e) {
      // Ignore unavailable storage.
    }

    try {
      return Boolean(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
    } catch (e) {
      return false;
    }
  };

  setEmbeddedDarkTheme = (enabled) => {
    if (!this.isEmbeddedInMvf()) return;

    const themeOverride = getMvfThemeOverride();
    if (themeOverride === 'dark' || themeOverride === 'light') return;

    this.persistMvfTheme(enabled ? 'dark' : 'light');
    document.body.classList.add('usecore-mvf-embedded');
    document.body.classList.toggle('usecore-mvf-dark', Boolean(enabled));
  };

  persistMvfTheme = (theme) => {
    if (theme !== 'dark' && theme !== 'light') return;

    try {
      if (window.localStorage) {
        window.localStorage.setItem('theme', theme);
      }
    } catch (e) {
      // Ignore unavailable storage.
    }
  };

  observeParentTheme = () => {
    if (this.parentThemeObserver || !window.parent || window.parent === window) return;

    try {
      const parentHtml = window.parent.document && window.parent.document.documentElement;
      if (!parentHtml || !window.MutationObserver) return;

      this.parentThemeObserver = new MutationObserver(() => {
        this.setEmbeddedDarkTheme(parentHtml.classList.contains('dark'));
      });
      this.parentThemeObserver.observe(parentHtml, {
        attributes: true,
        attributeFilter: ['class'],
      });
    } catch (e) {
      // Theme can still be delivered by postMessage.
    }
  };

  postMvfLocationChange = () => {
    if (!this.isEmbeddedInMvf() || window.parent === window) return;

    const pathname = window.location.pathname.startsWith('/legacy')
      ? window.location.pathname
      : `/legacy${window.location.pathname}`;

    window.parent.postMessage({
      type: 'USECORE_LOCATION_CHANGE',
      legacyPath: `${pathname}${window.location.search}${window.location.hash}`,
    }, window.location.origin);
  };

  postMvfReady = () => {
    // Sinaliza ao shell do MVF que uma rota REAL do app montou (não a tela de
    // bootstrap /started), para o iframe pré-aquecido trocar o skeleton pelo
    // conteúdo sem depender de heurística de contagem de nós no lado do shell.
    if (!this.isEmbeddedInMvf() || window.parent === window) return;

    const routePath = window.location.pathname.replace(/^\/legacy/, '') || '/';
    if (routePath === `/${routes.ROUTE_STARTED}` || routePath === '/') return;

    // Adia para depois do commit da rota (fim da pilha atual). Usamos
    // setTimeout e NÃO requestAnimationFrame: no frame pré-aquecido (oculto) o
    // rAF fica suspenso pela falta de pintura e o sinal nunca sairia.
    const emit = () => {
      if (window.parent === window) return;
      window.parent.postMessage({
        type: 'USECORE_READY',
        legacyPath: `${window.location.pathname}${window.location.search}`,
      }, window.location.origin);
    };

    window.setTimeout(emit, 0);
  };

  handleMvfNavigationMessage = (event) => {
    if (!this.isEmbeddedInMvf()) return;
    if (event.origin !== window.location.origin) return;

    const { data } = event;
    if (data && data.type === 'USECORE_THEME') {
      if (data.theme === 'dark' || data.theme === 'light') {
        this.setEmbeddedDarkTheme(data.theme === 'dark');
      }
      return;
    }

    if (!data || data.type !== 'USECORE_NAVIGATE' || typeof data.legacyNext !== 'string') {
      return;
    }

    const url = new URL(data.legacyNext, window.location.origin);
    if (!url.pathname.startsWith('/legacy/')) {
      return;
    }

    const path = url.pathname.replace(/^\/legacy/, '') || '/app';
    const searchParams = url.searchParams;
    searchParams.set('mvfEmbedded', '1');

    const nextPath = `${path}?${searchParams.toString()}`;
    const currentPath = `${window.location.pathname.replace(/^\/legacy/, '')}${window.location.search}`;

    if (nextPath !== currentPath) {
      this.props.history.push(nextPath);
      return;
    }

    this.postMvfLocationChange();
  };

  initMVFWidget = () => {
    const mvfBaseUrl = (
      process.env.REACT_APP_MVF_WIDGET_BASE_URL ||
      'https://valoremfisioterapia.com.br'
    ).replace(/\/+$/, '');

    window.MVFWidget = window.MVFWidget || {};
    window.MVFWidget.init = window.MVFWidget.init || function () {
      (window.MVFWidget.q = window.MVFWidget.q || []).push(arguments);
    };

    const authString = getCookie('auth');
    let auth = null;
    try {
      auth = authString ? JSON.parse(authString) : null;
    } catch (e) {
      // cookie malformado, trata como não autenticado
    }

    const widgetConfig = {
      app_id: process.env.REACT_APP_MVF_WIDGET_APP_ID,
    };

    if (auth && auth.token) {
      widgetConfig.user_jwt = auth.token;
      widgetConfig.user_id = auth.userId || "";
      widgetConfig.name = auth.name || auth.userName || "";
      widgetConfig.email = auth.email || "";
      widgetConfig.phone = auth.cel || "";
      widgetConfig.account_name = auth.accountName || "";
      widgetConfig.hash = auth.intercomUserHash || "";
    }

    window.MVFWidget.init(widgetConfig);

    const prevUserId = window.mvfSettings?.user?.id;

    // Mapeamento necessário para o src atual do webchat.js ler a base da API
    window.mvfSettings = {
      api_base: `${mvfBaseUrl}/api/public`,
      install_key: widgetConfig.app_id,
    };

    if (widgetConfig.user_jwt) {
      window.mvfSettings.jwt = widgetConfig.user_jwt;
      window.mvfSettings.user = {
        id: widgetConfig.user_id,
        name: widgetConfig.name,
        email: widgetConfig.email,
        phone: widgetConfig.phone,
        account_name: widgetConfig.account_name,
        hash: widgetConfig.intercom_user_hash,
      };
    }

    const currentUserId = window.mvfSettings?.user?.id;
    const authChanged = prevUserId !== currentUserId;

    const scriptId = 'mvf-webchat-widget';
    if (document.getElementById(scriptId)) {
      if (authChanged && window.MVF) {
        // Limpa a sessão local para não misturar chats entre usuários/visitantes
        localStorage.removeItem('mvf_chat_session');
        localStorage.removeItem('mvf_vid');

        // Desliga o widget atual e reinicia com as novas configurações
        window.MVF('shutdown');
        window.MVF('boot', window.mvfSettings);
      }
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'text/javascript';
    script.async = true;
    script.src = `${mvfBaseUrl}/widget/webchat.js`;

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
      return;
    }

    document.head.appendChild(script);
  };

  render() {
    return (
      <Fragment>
        <Switch>
          {/* <Route exact path={`${systemConfigs.AUTH_DOMAIN}`} component={SignInPage} /> */}
          <Route exact path={`/`} component={SignInPage} />
          {/* <Route path={`/${routes.REGISTER}`} component={RegisterPage} /> */}
          <Route exact path={`/${routes.RECOVER}`} component={RecoverPage} />
          <Route path={`/${routes.RECOVER}/:token`} component={PasswordPage} />
          <Route path={`/${routes.AUTOLOGIN}/:token`} component={AutoLogin} />
          {/* <LazyRouteProtectedName
            path={`/${routes.PURCHASE}`}
            component={() => import('@Pages/purchase')}
          /> */}
          <LazyRouteName
            path={`/${routes.DOCUMENT}/:sharedId`}
            component={() => import('@Pages/documentShared')} />
          <LazyRouteName
            path={`/${routes.FORMS}/${routes.ANSWER}/:attendanceAnswerId`}
            component={() => import('@Pages/attendanceAnswerPatient')} />
          <LazyRouteName
            path={`/${routes.SIGNATURE}/:signatoryId`}
            component={() => import('@Pages/signature')} />
          <LazyRouteName
            path={`/${routes.CHECK}/${routes.AUTHENTICITY}`}
            component={() => import('@Pages/authenticityCheck')} />
          <LazyRouteProtectedName
            path={`/${routes.STARTED}`}
            component={() => import('@Pages/started')}
          />
          <LazyRouteProtectedName
            path={`/${routes.ROUTE_BASIC}`}
            component={() => import('@Pages/dashboard')}
            drawer={'default'}
          />
          <LazyRouteProtectedName
            path={`/${routes.MANAGER}`}
            component={() => import('@Pages/managerBusiness')}
            drawer={'manager'}
          />
          <LazyRouteName
            path={`/${routes.INVITE}/:token`}
            component={() => import('@Pages/invite')}
          />
          <LazyRouteName
            path={`/${routes.PRE_REGISTRATION_PUBLIC}/:linkId`}
            component={() => import('@Pages/preRegistration')}
          />
          <Route
            // render={props => (window.location = systemConfigs.AUTH_DOMAIN)}
            render={(props) => (
              <Redirect
                to={{ pathname: '/', state: { from: props.location } }}
              />
            )}
          />
        </Switch>
        <NoPermissionDialog />
        <FieldModelDialog />
        <Messages />
        <SnackBar />
      </Fragment>
    );
  }
}

export default App;
